import type { IShowFunds } from 'global-stores';

/**
 * Row-shaping for the funds list.
 *
 * Everything here reads what the list already has. The stat line under the title
 * is summed from the rows ON THE PAGE — spec §4.1 — so it costs no request and
 * never claims to describe funds it cannot see.
 */

export interface IFundRow {
	id: string;
	name: string;
	investors: number;
	pending: number;
	raised: number;
	round: string;
	totalRaised: number;
	postMoney: number;
}

const num = (v: unknown): number => {
	const n = Number(v ?? 0);
	return Number.isFinite(n) ? n : 0;
};

export const toFundRows = (rows: IShowFunds[] | any[]): IFundRow[] =>
	(rows ?? []).map((r: any) => ({
		id: r?.id ?? '',
		name: r?.name ?? 'Untitled fund',
		investors: num(r?.investor),
		pending: num(r?.pending),
		// `raised` is stashed alongside the jsx cell by the existing row builder;
		// `moneyRaised` is that cell, so it cannot be read as a number.
		raised: num(r?.raised ?? r?.moneyRaised),
		round: r?.round && r.round !== 0 ? String(r.round) : '',
		totalRaised: num(r?.totalMoneyRaised),
		postMoney: num(r?.postMoneyValuation),
	}));

/** "6 investors · 2 pending approval" — the two columns that used to be mostly 0. */
export const investorLine = (row: IFundRow): string => {
	if (!row.investors) return 'no investors yet';
	const inv = `${row.investors} investor${row.investors === 1 ? '' : 's'}`;
	if (!row.pending) return `${inv} · no pending approvals`;
	return `${inv} · ${row.pending} pending approval${row.pending === 1 ? '' : 's'}`;
};

/** Totals for the page's own sub-line. Summed from what is rendered, nothing more. */
export const pageTotals = (rows: IFundRow[]) =>
	rows.reduce(
		(acc, r) => ({
			raised: acc.raised + r.totalRaised,
			investors: acc.investors + r.investors,
		}),
		{ raised: 0, investors: 0 }
	);

export type SortKey =
	| 'name'
	| 'investors'
	| 'raised'
	| 'totalRaised'
	| 'postMoney';

/**
 * Page-local sort, and the footnote says so. Server-side ordering is §5.2 and
 * needs the list endpoint to accept a sort — until then, claiming otherwise
 * would be the lie the old table told.
 */
export const sortRows = (
	rows: IFundRow[],
	key: SortKey,
	dir: 1 | -1
): IFundRow[] =>
	[...rows].sort((a, b) => {
		const av = a[key];
		const bv = b[key];
		if (typeof av === 'string' || typeof bv === 'string') {
			return String(av).localeCompare(String(bv)) * dir;
		}
		return ((av as number) - (bv as number)) * dir;
	});
